import { Flex } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { Spending } from '../../../api/financials-client/financials-client.type';
import { OverviewCard } from '../../../components/overview-card/overview-card';
import { formatCurrency, getCurrency } from '../../../helpers/currency-helpers';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { skyDark } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { useWorkPeriodsData } from '../../work-periods/hooks/work-periods.hooks';
import { EditView } from '../financials';
import {
  getAverageMonthlySpendingPerProject,
  getMaximumMonthlySpend as getMaximumMonthlySpendByDate,
  getMedianMonthlySpendingPerProject,
  getMinimumMonthlySpend as getMinimumMonthlySpendByDate,
  getTotalSpending as getTotalSpendingByDate,
} from './by-date.helpers';
import {
  getAverageSpendingPerSprint,
  getMaximumSpendingPerSprint,
  getMedianSpendingPerSprint,
  getMinimumSpendingPerSprint,
  getTotalSpending as getTotalSpendingBySprint,
} from './by-sprint.helpers';

function Overview({ spendingData, editView }: { spendingData: Spending[] | undefined; editView: EditView }) {
  return editView === EditView.ByDate ? <ByDate spendingData={spendingData} /> : <BySprint />;
}

function ByDate({ spendingData }: { spendingData: Spending[] | undefined }) {
  const { t } = useTranslation();
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  const totalBudget = getTotalSpendingByDate(spendingData, selectedYear);
  const averageMonthly = getAverageMonthlySpendingPerProject(spendingData, selectedYear);
  const medianMonthly = getMedianMonthlySpendingPerProject(spendingData, selectedYear);
  const minimumMonthly = getMinimumMonthlySpendByDate(spendingData, selectedYear);
  const maximumMonthly = getMaximumMonthlySpendByDate(spendingData, selectedYear);

  return (
    <Flex direction="row" gap={16} wrap="wrap">
      <FinancialCard
        title={t('financials.overview.cards.total_budget')}
        icon="payments"
        value={totalBudget !== null ? formatCurrency(totalBudget, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.all_teams_combined')}
      />
      <FinancialCard
        title={t('financials.overview.cards.average_monthly')}
        icon="calendar_month"
        value={averageMonthly !== null ? formatCurrency(averageMonthly, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.per_team_average')}
      />
      <FinancialCard
        title={t('financials.overview.cards.minimum_spend')}
        icon="south_east"
        value={minimumMonthly !== null ? formatCurrency(minimumMonthly, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.minimum_monthly_spending')}
      />
      <FinancialCard
        title={t('financials.overview.cards.median_monthly_spend')}
        icon="arrow_upward"
        value={medianMonthly !== null ? formatCurrency(medianMonthly, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.per_team_median')}
      />
      <FinancialCard
        title={t('financials.overview.cards.highest_spend')}
        icon="north_east"
        value={maximumMonthly !== null ? formatCurrency(maximumMonthly, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.peak_monthly_spending')}
      />
    </Flex>
  );
}

function BySprint() {
  const { t } = useTranslation();
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);
  const selectedBoard = useFinancialsStore((state) => state.board);
  const { data: workPeriods } = useWorkPeriodsData(selectedBoard);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  const totalBudget = getTotalSpendingBySprint(workPeriods, selectedYear);
  const averagePerSprint = getAverageSpendingPerSprint(workPeriods, selectedYear);
  const medianPerSprint = getMedianSpendingPerSprint(workPeriods, selectedYear);
  const minimumPerSprint = getMinimumSpendingPerSprint(workPeriods, selectedYear);
  const maximumPerSprint = getMaximumSpendingPerSprint(workPeriods, selectedYear);

  return (
    <Flex direction="row" gap={16} wrap="wrap">
      <FinancialCard
        title={t('financials.overview.cards.total_budget')}
        icon="payments"
        value={totalBudget !== null ? formatCurrency(totalBudget, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.all_sprints_combined')}
      />
      <FinancialCard
        title={t('financials.overview.cards.average_per_sprint')}
        icon="calendar_month"
        value={averagePerSprint !== null ? formatCurrency(averagePerSprint, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.per_sprint_average')}
      />
      <FinancialCard
        title={t('financials.overview.cards.minimum_per_sprint_spend')}
        icon="south_east"
        value={minimumPerSprint !== null ? formatCurrency(minimumPerSprint, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.minimum_per_sprint_spending')}
      />
      <FinancialCard
        title={t('financials.overview.cards.median_per_sprint_spend')}
        icon="arrow_upward"
        value={medianPerSprint !== null ? formatCurrency(medianPerSprint, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.per_sprint_median')}
      />
      <FinancialCard
        title={t('financials.overview.cards.highest_per_sprint_spend')}
        icon="north_east"
        value={maximumPerSprint !== null ? formatCurrency(maximumPerSprint, { currency: currencyCode }) : '--'}
        description={t('financials.overview.cards.descriptions.peak_per_sprint_spending')}
      />
    </Flex>
  );
}

function FinancialCard({
  title,
  icon,
  value,
  description,
}: {
  title: string;
  icon: string;
  value: string;
  description: string;
}) {
  const content = (
    <Flex direction="column" gap={8} h="100%" justify="center" align="center" mih={80}>
      <Text size="large" weight="bold" style={{ textAlign: 'center' }}>
        {value}
      </Text>
      <Text size="small" color={skyDark} style={{ textAlign: 'center' }}>
        {description}
      </Text>
    </Flex>
  );

  return <OverviewCard title={title} icon={icon} content={content} />;
}

export { Overview };
